// KvkkDeletionQueuePage — wired to real backend (S13, Agent U).
//
// Admin-side queue of KVKK deletion requests. The BE supports list, get,
// and force-execute (with justification). There is no admin "cancel"; cancel
// is member-side only. The "Şimdi Sil" button is RED with a double-confirm
// modal explaining the cooldown bypass.

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Grid,
  Typography,
} from 'antd';
import { AlertOctagon, ShieldX, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { kvkkApi } from '@api/kvkk';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import type { KvkkDeletionRequest } from '@features/kvkk/types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface Countdown {
  days: number;
  expired: boolean;
  tone: 'danger' | 'warning' | 'neutral';
}

function computeCountdown(scheduledIso: string): Countdown {
  const now = dayjs();
  const scheduled = dayjs(scheduledIso);
  const diffDays = scheduled.diff(now, 'day');
  if (diffDays < 0 || (diffDays === 0 && scheduled.isBefore(now))) {
    return { days: diffDays, expired: true, tone: 'danger' };
  }
  if (diffDays < 7) {
    return { days: diffDays, expired: false, tone: 'warning' };
  }
  return { days: diffDays, expired: false, tone: 'neutral' };
}

export function KvkkDeletionQueuePage() {
  const { t } = useTranslation(['kvkk', 'common', 'validation', 'errors']);
  const td = t as unknown as (key: string) => string;
  const countdownText = (cd: Countdown) =>
    cd.expired
      ? t('queue.countdown.expired')
      : t('queue.countdown.daysLeft', { count: cd.days });
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.KVKK_DELETION_MANAGE);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<KvkkDeletionRequest['status'] | 'ALL'>(
    'ALL',
  );

  // Double-confirm state
  const [executeTarget, setExecuteTarget] =
    useState<KvkkDeletionRequest | null>(null);
  const [confirmStep, setConfirmStep] = useState<1 | 2>(1);
  const [justification, setJustification] = useState('');

  // ISSUE-52 — admin cancel state
  const [cancelTarget, setCancelTarget] =
    useState<KvkkDeletionRequest | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: ['kvkk-deletions', { page, pageSize, status }],
    queryFn: () => kvkkApi.list({ page, pageSize, status }),
    placeholderData: (prev) => prev,
  });

  const executeMutation = useMutation({
    mutationFn: (target: KvkkDeletionRequest) =>
      kvkkApi.execute(target.id, true, justification.trim() || 'Admin force-execute'),
    onSuccess: async (req) => {
      toast.success(t('queue.toast.executed'));
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['kvkk-deletions'] }),
        qc.invalidateQueries({ queryKey: ['app-users'] }),
        qc.invalidateQueries({ queryKey: ['app-user', req.appUserId] }),
      ]);
      void recordAuditAction({
        action: 'kvkk.forceExecute',
        resource: 'kvkk',
        resourceId: req.id,
        summary: t('queue.audit.forceExecute', { name: req.appUserName }),
        meta: { justification },
      });
      setExecuteTarget(null);
      setConfirmStep(1);
      setJustification('');
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  // ISSUE-52 — admin cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (target: KvkkDeletionRequest) =>
      kvkkApi.adminCancel(target.id, cancelReason.trim() || undefined),
    onSuccess: async (req) => {
      toast.success(t('queue.toast.cancelled'));
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['kvkk-deletions'] }),
        qc.invalidateQueries({ queryKey: ['app-user', req.appUserId] }),
      ]);
      void recordAuditAction({
        action: 'kvkk.adminCancel',
        resource: 'kvkk',
        resourceId: req.id,
        summary: t('queue.audit.adminCancel', { name: req.appUserName }),
        meta: { reason: cancelReason },
      });
      setCancelTarget(null);
      setCancelReason('');
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const columns = useMemo(
    () => [
      {
        key: 'appUserName',
        title: t('queue.table.columns.user'),
        dataIndex: 'appUserName',
        render: (_: unknown, row: KvkkDeletionRequest) => (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontWeight: 600 }}>{row.appUserName}</div>
            <div style={{ fontSize: 11, color: 'var(--ds-text-secondary)', fontFamily: 'monospace' }}>
              {row.appUserId.slice(0, 8)}…
            </div>
          </div>
        ),
      },
      {
        key: 'requestedAt',
        title: t('queue.table.columns.requestedAt'),
        dataIndex: 'requestedAt',
        width: 140,
        responsive: ['md' as const],
        render: (v: string) => (
          <span style={{ fontSize: 12 }}>{dayjs(v).format('DD.MM.YYYY')}</span>
        ),
      },
      {
        key: 'scheduledHardDeleteAt',
        title: t('queue.table.columns.scheduledHardDeleteAt'),
        dataIndex: 'scheduledHardDeleteAt',
        width: 220,
        render: (v: string, row: KvkkDeletionRequest) => {
          const cd = computeCountdown(v);
          if (row.status !== 'PENDING') {
            return (
              <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                {dayjs(v).format('DD.MM.YYYY')}
              </span>
            );
          }
          return (
            <Space size={6} wrap>
              <span style={{ fontSize: 12 }}>{dayjs(v).format('DD.MM.YYYY')}</span>
              <Tag
                color={
                  cd.tone === 'danger'
                    ? 'red'
                    : cd.tone === 'warning'
                      ? 'orange'
                      : 'default'
                }
              >
                {countdownText(cd)}
              </Tag>
            </Space>
          );
        },
      },
      {
        key: 'reason',
        title: t('queue.table.columns.reason'),
        dataIndex: 'reason',
        responsive: ['lg' as const],
        render: (v: string | undefined) =>
          v ? (
            <span style={{ fontSize: 12 }}>{v}</span>
          ) : (
            <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
          ),
      },
      {
        key: 'status',
        title: t('common:labels.status'),
        dataIndex: 'status',
        width: 130,
        render: (s: KvkkDeletionRequest['status']) => {
          if (s === 'PENDING')
            return <StatusBadge tone="warning" label={td('status.PENDING')} />;
          if (s === 'CANCELLED')
            return <StatusBadge tone="neutral" label={td('status.CANCELLED')} />;
          return <StatusBadge tone="success" label={td('status.COMPLETED')} />;
        },
      },
      {
        key: 'actions',
        title: '',
        width: 240,
        render: (_: unknown, row: KvkkDeletionRequest) => {
          if (row.status !== 'PENDING') {
            return (
              <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>
                {row.completedAt
                  ? t('queue.table.completedAt', {
                      date: dayjs(row.completedAt).format('DD.MM.YYYY'),
                    })
                  : '—'}
              </span>
            );
          }
          if (!canManage) return null;
          return (
            <Space size={4} wrap>
              <Tooltip title={t('queue.actions.cancelTooltip')}>
                <Button
                  size="small"
                  icon={<XCircle size={14} />}
                  onClick={() => {
                    setCancelTarget(row);
                    setCancelReason('');
                  }}
                >
                  {t('queue.actions.cancel')}
                </Button>
              </Tooltip>
              <Tooltip title={t('queue.actions.executeTooltip')}>
                <Button
                  size="small"
                  danger
                  icon={<ShieldX size={14} />}
                  onClick={() => {
                    setExecuteTarget(row);
                    setConfirmStep(1);
                    setJustification('');
                  }}
                >
                  {t('queue.actions.execute')}
                </Button>
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, t],
  );

  const overdueCount = useMemo(
    () =>
      items.filter(
        (r) =>
          r.status === 'PENDING' &&
          computeCountdown(r.scheduledHardDeleteAt).tone === 'danger',
      ).length,
    [items],
  );

  return (
    <div>
      <PageHeader
        title={t('queue.page.title')}
        description={t('queue.page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('queue.breadcrumb.users') },
          { label: t('queue.breadcrumb.list') },
        ]}
      />

      {overdueCount > 0 ? (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          icon={<AlertOctagon size={16} />}
          message={t('queue.overdue.message', { count: overdueCount })}
          description={t('queue.overdue.description')}
        />
      ) : null}

      <Card>
        <DataTable<KvkkDeletionRequest>
          data={items}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          exportable
          exportFileName={t('queue.exportFileName')}
          filters={
            <Space wrap>
              <Segmented<KvkkDeletionRequest['status'] | 'ALL'>
                size="small"
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
                options={[
                  { value: 'ALL', label: t('common:labels.all') },
                  { value: 'PENDING', label: td('status.PENDING') },
                  { value: 'CANCELLED', label: td('status.CANCELLED') },
                  { value: 'COMPLETED', label: td('status.COMPLETED') },
                ]}
              />
            </Space>
          }
          pagination={{
            page,
            size: pageSize,
            total: data?.total ?? 0,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>

      {/* Double-confirm modal for force-execute */}
      <Modal
        title={
          confirmStep === 1
            ? t('queue.executeModal.titleStep1', {
                name: executeTarget?.appUserName ?? '',
              })
            : t('queue.executeModal.titleStep2', {
                name: executeTarget?.appUserName ?? '',
              })
        }
        open={!!executeTarget}
        onCancel={() => {
          setExecuteTarget(null);
          setConfirmStep(1);
          setJustification('');
        }}
        confirmLoading={executeMutation.isPending}
        okText={
          confirmStep === 1
            ? t('queue.executeModal.okStep1')
            : t('queue.executeModal.okStep2')
        }
        okButtonProps={{
          danger: true,
          disabled:
            confirmStep === 2 && justification.trim().length < 10,
        }}
        cancelText={t('common:actions.cancel')}
        onOk={() => {
          if (confirmStep === 1) setConfirmStep(2);
          else if (executeTarget) executeMutation.mutate(executeTarget);
        }}
        width={isMobile ? '95%' : 520}
      >
        {confirmStep === 1 ? (
          <Alert
            type="error"
            showIcon
            icon={<AlertOctagon size={16} />}
            message={t('queue.executeModal.step1Message')}
            description={t('queue.executeModal.step1Description')}
          />
        ) : (
          <div>
            <Alert
              type="error"
              showIcon
              message={t('queue.executeModal.step2Message')}
              description={
                <Text>
                  <strong>{executeTarget?.appUserName}</strong>
                  {t('queue.executeModal.step2DescriptionAfter')}
                </Text>
              }
              style={{ marginBottom: 12 }}
            />
            <Input.TextArea
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t('queue.executeModal.justificationPlaceholder')}
              maxLength={500}
              showCount
              // Modal, kullanıcı aksiyonuyla açılır; gerekçe alanına odak
              // vermek bu bağlamda beklenen davranıştır (a11y sorunu değil).
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>
        )}
      </Modal>

      {/* ISSUE-52 — admin cancel modal */}
      <Modal
        title={t('queue.cancelModal.title', {
          name: cancelTarget?.appUserName ?? '',
        })}
        open={!!cancelTarget}
        onCancel={() => {
          setCancelTarget(null);
          setCancelReason('');
        }}
        confirmLoading={cancelMutation.isPending}
        okText={t('queue.cancelModal.ok')}
        cancelText={t('common:actions.cancel')}
        onOk={() => {
          if (cancelTarget) cancelMutation.mutate(cancelTarget);
        }}
        width={isMobile ? '95%' : 480}
      >
        <Alert
          type="info"
          showIcon
          message={t('queue.cancelModal.message')}
          description={t('queue.cancelModal.description')}
          style={{ marginBottom: 12 }}
        />
        <Input.TextArea
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={t('queue.cancelModal.reasonPlaceholder')}
          maxLength={500}
          showCount
        />
      </Modal>
    </div>
  );
}
